/* eslint-disable @typescript-eslint/no-explicit-any */
import axios, {
  AxiosInstance,
  AxiosInterceptorManager,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios';
import { generateStorage } from 'shared/lib';
import { LoginStorage } from 'shared/lib';

import { ADMIN, JWT_KEY } from '../../shared/constants/auth';

export type BaseResponse<T = any> = {
  isSuccess: boolean;
  message: string;
  result: T;
};

export type TokenReissueResponse = {
  isSuccess: boolean;
  code: number;
  message: string;
  result: {
    accessToken: string;
  };
};

interface CustomInstance extends AxiosInstance {
  interceptors: {
    request: AxiosInterceptorManager<InternalAxiosRequestConfig>;
    response: AxiosInterceptorManager<AxiosResponse<BaseResponse>>;
  };
  getUri(config?: AxiosRequestConfig): string;
  request<T>(config: AxiosRequestConfig): Promise<T>;
  get<T>(url: string, config?: AxiosRequestConfig): Promise<T>;
  delete<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T>;
  head<T>(url: string, config?: AxiosRequestConfig): Promise<T>;
  options<T>(url: string, config?: AxiosRequestConfig): Promise<T>;
  post<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T>;
  put<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T>;
  patch<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T>;
}

const safeLoginStorage = generateStorage();

export const request: CustomInstance = axios.create({
  baseURL: process.env.REACT_APP_API,
  timeout: 20000,
  headers: {
    accept: 'application/json',
    Authorization: `Bearer ${safeLoginStorage.get(JWT_KEY)}`,
  },
  withCredentials: true,
});

request.interceptors.request.use(
  (config) => {
    const jwt = safeLoginStorage.get(JWT_KEY);
    if (jwt) config.headers.Authorization = `Bearer ${jwt}`;
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// TODO: 성공시 response 만 반환하도록 처리

request.interceptors.response.use(
  // TODO: isSuccess만 보내지 않도록 처리
  (response) => {
    if (response.data.isSuccess) {
      return response.data.result;
    } else {
      return Promise.reject(response.data.message);
    }
  },
  async (error) => {
    const originalRequest = error.config;
    const requestUrl: string = originalRequest?.url ?? '';

    // 토큰 재발급(reissue)을 시도하면 안 되는 엔드포인트.
    // - 로그인/가입 전이라 만료 세션이 아니며, 401/403은 "세션 만료"가 아니라
    //   비밀번호 불일치·가입 승인 대기/거절 같은 "업무상 에러"이기 때문이다.
    //   reissue를 거치면 원래 에러 메시지가 삼켜지고 강제 로그아웃된다.
    const NO_REISSUE_PATHS = [
      '/auth/admin/sign-up/form',
      '/auth/admin/sign-up/social',
      '/admin/info',
      '/auth/admin/login/form',
      '/auth/admin/login/kakao',
    ];
    const skipReissue = NO_REISSUE_PATHS.some((path) => requestUrl.startsWith(path));

    if ((error.response?.status === 401 || error.response?.status === 403) && !originalRequest._retry && !skipReissue) {
      originalRequest._retry = true;

      try {
        const { data } = await axios.post<TokenReissueResponse>(
          `${process.env.REACT_APP_API}/auth/token/reissue/admin`,
          {},
          { withCredentials: true },
        );

        // NOTE: http code is returned as 200 even if the request is failed. so we need to check isSuccess
        // NOTE: 1. refresh  토큰이 만료되면 아래 조건문이 실행됩니다.
        if (!data.isSuccess) {
          // NOTE: user should be logged out even when token is expired
          // NOTE: 2. refresh 토큰이 만료된 상태에서 로그아웃 요청을 보내면 400번대 에러가 발생하기 때문에 에러를 던지게 됩니다
          await axios.post(`${process.env.REACT_APP_API}/auth/logout`);
        }

        const newAccessToken = data.result.accessToken;

        safeLoginStorage.set(JWT_KEY, newAccessToken);

        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;

        return request(originalRequest);
      } catch (reissueError) {
        // NOTE: 3. 에러를 전달받고, 메인 페이지로 이동하도록 합니다

        alert('인증이 만료되었습니다. 다시 로그인해주세요.');
        safeLoginStorage.remove(ADMIN);
        LoginStorage.removeToken();

        window.location.href = '/login';
      }
    }

    // 백엔드 공통 래퍼({ isSuccess, code, message, result })의 message를 그대로 노출한다.
    // (성공 200·isSuccess:false 경로와 동일하게 문자열 메시지로 reject → 핸들러의 alert(error)에서 안내됨)
    // 예: 403 "가입 승인 대기 중입니다. 승인 후 로그인할 수 있습니다." / "가입이 거절되었습니다. ..."
    if (error.code === 'ERR_NETWORK') {
      return Promise.reject('허용되지 않은 네트워크 접근입니다.');
    }
    return Promise.reject(error.response?.data?.message ?? error);
  },
);

export default request;
